import { DndContext, DragEndEvent } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useFormik } from 'formik'
import React from 'react'
import { toast } from 'react-toastify'
import ModalComponent from '../..'
import { COLLECTION_WEBSITE_OPTIONS } from '../../../../constants'
import { deepCopy, moveElement } from '../../../../helpers'
import {
  useGetAffiliateCollectionQuery,
  useGetAffiliateQuery,
  useUpsertAffiliateCollectionMutation,
} from '../../../../redux-toolkits/loyalty/loyalty.slice'
import {
  CollectionItem,
  UpsertCollectionPayload,
} from '../../../../redux-toolkits/loyalty/loyalty.type'
import { Article } from '../../../../redux-toolkits/lists/list.types'
import {
  AffiliateCollectionSchema,
  affiliateCollectionLocationSchema,
} from '../../../../validation/validate'
import { Button } from '../../../button'
import Icon from '../../../icon'
import ComboBox from '../../../inputs/combobox'
import { TextInput } from '../../../inputs/textInput'
import Spinner from '../../../spinner/Spinner'
import { AddLocationModal } from '../addLocationModal'

type CollectionFormItem = {
  articleId: string
  title: string
  bannerUrl?: string
  sourceUrl?: string
}

/**
 * Converts a coordinate value from form/API shape into an upsert-safe numeric string.
 * Blank values mean the collection has no location yet, while invalid values are
 * omitted so we do not send `NaN` to the API.
 */
const toOptionalCoordinateString = (
  coordinate: number | string | undefined,
): string | undefined => {
  if (coordinate === undefined || coordinate === '') return undefined

  const parsedCoordinate = Number(coordinate)
  return Number.isFinite(parsedCoordinate)
    ? String(parsedCoordinate)
    : undefined
}

const SortableItem: React.FC<{
  item: CollectionFormItem
  onRemove: () => void
  onSourceUrlChange: (sourceUrl?: string) => void
}> = ({ item, onRemove, onSourceUrlChange }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.articleId })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 rounded-md border border-divider-gray p-3 bg-white"
    >
      <button
        type="button"
        className="cursor-grab touch-none"
        {...attributes}
        {...listeners}
      >
        <Icon id="rearrange" width={24} height={24} />
      </button>

      {item.bannerUrl && (
        <img
          src={item.bannerUrl}
          alt={item.title}
          className="h-10 w-10 min-w-[40px] rounded object-cover"
        />
      )}

      <div className="flex flex-1 flex-col gap-2 overflow-hidden">
        <span className="truncate text-sm font-normal text-black">
          {item.title}
        </span>

        <ComboBox
          type="text"
          name={`sourceUrl-${item.articleId}`}
          value={
            COLLECTION_WEBSITE_OPTIONS.find(
              (website) => website.value === item.sourceUrl,
            )?.name ?? ''
          }
          placeholder="Select source website"
          hasIcon={false}
          menuItems={COLLECTION_WEBSITE_OPTIONS}
          onSelectOption={(sourceUrl?: string) => {
            onSourceUrlChange(sourceUrl)
          }}
          immediate
          position="bottom start"
          menuClassName="!max-h-[160px] !z-[999] shadow-md"
          className="w-full"
          isDropdown
        />
      </div>

      <button
        type="button"
        onClick={onRemove}
        className="rounded-full bg-danger p-1.5"
      >
        <Icon id="bin" width={16} height={16} className="text-white" />
      </button>
    </div>
  )
}

export const ManageCollectionModal: React.FC<{
  open: boolean
  setOpen: (val: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: () => void
  affiliateId?: string
}> = ({
  open,
  setOpen,
  onCloseCallback = () => {},
  onSuccessCallback = () => {},
  affiliateId,
}) => {
  const [locationModalOpen, setLocationModalOpen] = React.useState(false)

  const { data: affiliateDetails, isFetching: fetchingAffiliate } =
    useGetAffiliateQuery(
      { id: affiliateId! },
      { refetchOnMountOrArgChange: true, skip: !affiliateId || !open },
    )

  const { currentData: collectionData, isFetching: fetchingCollection } =
    useGetAffiliateCollectionQuery(
      { affiliateId: affiliateId! },
      { refetchOnMountOrArgChange: true, skip: !affiliateId || !open },
    )

  const [upsertCollection, { isLoading: saving }] =
    useUpsertAffiliateCollectionMutation()

  const {
    handleChange,
    setFieldValue,
    handleBlur,
    handleSubmit,
    values,
    isValid,
    resetForm,
    touched,
    errors,
  } = useFormik({
    initialValues: {
      title: collectionData?.data?.title || '',
      description: collectionData?.data?.description || '',
      lat: collectionData?.data?.latitude?.toString() || '',
      lng: collectionData?.data?.longitude?.toString() || '',
      items:
        collectionData && (collectionData?.data?.items?.length ?? 0) > 0
          ? [...collectionData.data.items]
              .sort((a, b) => a.position - b.position)
              .map((item: CollectionItem) => ({
                articleId: item.article._id,
                title: item.article.title,
                bannerUrl: item.article.bannerUrl,
                sourceUrl: item.sourceUrl,
              }))
          : [],
    },
    enableReinitialize: true,
    validationSchema: AffiliateCollectionSchema,
    onSubmit: async (values) => {
      const lat = toOptionalCoordinateString(values.lat)
      const lng = toOptionalCoordinateString(values.lng)
      const payload: UpsertCollectionPayload = {
        title: values.title,
        description: values.description,
        items: values.items.map((item, idx) => {
          const payloadItem = {
            article: item.articleId,
            position: idx,
            sourceUrl: item.sourceUrl || undefined,
          }

          return payloadItem
        }),
      }

      if (lat !== undefined && lng !== undefined) {
        payload.latitude = lat
        payload.longitude = lng
      }

      const resp = await upsertCollection({
        affiliateId: affiliateId!,
        payload,
      })

      if (resp.data?.success) {
        toast.success(resp.data.message)
        resetForm()
        onSuccessCallback()
      }
    },
  })

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e
    if (over && active.id !== over.id) {
      const dataCopy = deepCopy(values.items)
      const oldIndex = dataCopy.findIndex(
        (item) => item.articleId === active.id,
      )
      const newIndex = dataCopy.findIndex((item) => item.articleId === over.id)
      setFieldValue('items', moveElement(dataCopy, oldIndex, newIndex))
    }
  }

  const handleAddItem = (selected: {
    id: string
    name: string
    bannerUrl?: string
  }) => {
    if (!selected?.id) return
    setFieldValue('items', [
      ...values.items,
      {
        articleId: selected.id,
        title: selected.name,
        bannerUrl: selected.bannerUrl,
      },
    ])
  }

  const handleRemoveItem = (articleId: string) => {
    setFieldValue(
      'items',
      values.items.filter((item) => item.articleId !== articleId),
    )
  }

  const handleSourceUrlChange = (articleId: string, sourceUrl?: string) => {
    setFieldValue(
      'items',
      values.items.map((item) => {
        if (item.articleId !== articleId) return item

        return {
          ...item,
          sourceUrl,
        }
      }),
    )
  }

  const affiliateLists: Article[] = affiliateDetails?.data?.lists ?? []
  const hasNoLists = !fetchingAffiliate && affiliateLists.length === 0
  const availableLists = affiliateLists.filter(
    (list) => !values.items.some((item) => item.articleId === list._id),
  )

  const isModalLoading = fetchingAffiliate || fetchingCollection
  const selectedLocation =
    values.lat && values.lng ? `${values.lat}, ${values.lng}` : undefined

  return (
    <>
      <ModalComponent
        open={open}
        setOpen={setOpen}
        onCloseCallback={() => {
          resetForm()
          onCloseCallback()
        }}
        title={collectionData ? 'Manage Collection' : 'Create Collection'}
        childrenClassName="md:!max-w-[480px]"
      >
        {isModalLoading ? (
          <Spinner fullScreen={false} className="py-10" />
        ) : hasNoLists ? (
          <div className="flex flex-col items-center gap-4 py-10 text-center">
            <p className="text-base text-sec-black">
              This affiliate has no lists. Add lists to the affiliate before
              managing a collection.
            </p>
          </div>
        ) : (
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            <TextInput
              type="text"
              name="title"
              title="Title"
              value={values.title}
              onChange={handleChange}
              onBlur={handleBlur}
              errors={touched.title && errors.title ? errors.title : ''}
              placeholder="Collection title"
              hasIcon={false}
            />

            <TextInput
              type="textarea"
              name="description"
              title="Description"
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
              errors={
                touched.description && errors.description
                  ? errors.description
                  : ''
              }
              placeholder="Collection description"
              hasIcon={false}
              rows={3}
            />

            <div className="flex flex-col gap-2">
              <p className="text-base font-medium text-black">
                Collection location
              </p>
              <p className="text-sm text-sec-black">
                {selectedLocation
                  ? `Selected coordinates: ${selectedLocation}`
                  : 'No collection location selected'}
              </p>
              <Button
                label={selectedLocation ? 'Change location' : 'Add location'}
                type="button"
                variant="border-grey"
                className="w-full"
                onClick={() => {
                  setLocationModalOpen(true)
                }}
              />
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-base font-medium text-black">Items</p>

              {values.items.length > 0 ? (
                <DndContext
                  onDragEnd={handleDragEnd}
                  modifiers={[restrictToVerticalAxis]}
                >
                  <SortableContext
                    items={values.items.map((item) => ({
                      id: item.articleId,
                    }))}
                  >
                    <div className="flex flex-col gap-2 mt-2">
                      {values.items.map((item) => (
                        <SortableItem
                          key={item.articleId}
                          item={item}
                          onRemove={() => handleRemoveItem(item.articleId)}
                          onSourceUrlChange={(sourceUrl) => {
                            handleSourceUrlChange(item.articleId, sourceUrl)
                          }}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              ) : (
                <p className="text-sm text-sec-black py-2">
                  No items added yet. Use the dropdown below to add good lists.
                </p>
              )}

              {availableLists.length > 0 && (
                <ComboBox
                  type="text"
                  name="addItem"
                  value=""
                  placeholder="Add a good list from affiliate's lists"
                  hasIcon={false}
                  menuItems={availableLists.map((list) => ({
                    name: list.title,
                    value: {
                      id: list._id,
                      name: list.title,
                      bannerUrl: list.bannerUrl,
                    },
                  }))}
                  onSelectOption={handleAddItem}
                  immediate
                  position="bottom start"
                  menuClassName="!max-h-[200px] !z-[999] shadow-md"
                  className="w-full"
                  errors={
                    touched.items && errors.items
                      ? (errors.items as string)
                      : ''
                  }
                  isDropdown
                />
              )}
            </div>

            <Button
              label={collectionData ? 'Manage collection' : 'Create changes'}
              type="submit"
              className="mt-2 w-full"
              disabled={!isValid || saving}
              loading={saving}
              primary
            />
          </form>
        )}
      </ModalComponent>

      <AddLocationModal
        open={locationModalOpen}
        setOpen={setLocationModalOpen}
        onSuccessCallback={(locationData, resetLocationForm) => {
          setFieldValue('lat', locationData.lat)
          setFieldValue('lng', locationData.long)
          resetLocationForm()
          setLocationModalOpen(false)
        }}
        onCloseCallback={() => {
          setLocationModalOpen(false)
        }}
        title="Collection Location"
        fields={['address', 'coordinates']}
        schema={affiliateCollectionLocationSchema}
        loading={false}
        placeholderPrefix="Collection"
      />
    </>
  )
}
